import {useDispatch, useSelector} from "react-redux";
import {Col, Modal as AntModal, notification, Row, Tag, Descriptions} from "antd";
import {useEffect, useState} from "react";
import {Button, Icon, Loading, Modal} from "../../../components";
import {BuilderLicenseTable, BuilderLicenseSearchBar, BuilderLicenseSearchMobile} from "../components";
import {
    builderLicenseActions,
    builderLicenseErrorSelector,
    builderLicenseFilterSelector,
    builderLicenseItemsSelector,
    builderLicenseLoadingSelector,
    builderLicensePaginationSelector
} from "../builderLicenseSlice";
import ActionBar from "../../../layout/ActionBar";
import {useCan, useDevice} from "../../../hooks";
import {apiError, handleRequest, renderDate} from "../../../utils";
import {builderLicenseApi} from "../../../api";

const statusConfig = {
    0: {color: 'orange', label: 'Chờ duyệt'},
    1: {color: 'green',  label: 'Đã duyệt'},
    2: {color: 'red',    label: 'Từ chối'},
};

function BuilderLicense()
{
    const can = {
        delete: useCan('builderLicenseDelete'),
        status: useCan('builderLicenseStatus'),
    };

    const dispatch = useDispatch();
    const {isMobile} = useDevice();

    const items      = useSelector(builderLicenseItemsSelector);
    const loading    = useSelector(builderLicenseLoadingSelector);
    const error      = useSelector(builderLicenseErrorSelector);
    const pagination = useSelector(builderLicensePaginationSelector);
    const filter     = useSelector(builderLicenseFilterSelector);

    const [openModal, setOpenModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const eventsHandler = {
        load: () => {
            dispatch(builderLicenseActions.fetchData({...filter}));
        },
        changePagination: (page) => {
            dispatch(builderLicenseActions.setFilter({...filter, page}));
        },
        handleSearchChange: (newFilter) => {
            dispatch(builderLicenseActions.setFilterWithDebounce(newFilter));
        },
        handleFilterChange: (key, value) => {
            const newFilter = {
                ...filter,
                [key]: value,
                page: 1
            };
            dispatch(builderLicenseActions.setFilter(newFilter));
        },
        viewDetail: (item) => {
            setSelectedItem(item);
            setOpenModal(true);
        },
        closeModal: () => {
            setOpenModal(false);
            setSelectedItem(null);
        },
        approve: (item) => {
            AntModal.confirm({
                title: 'Xác nhận duyệt',
                content: `Duyệt license cho website "${item.websiteName}"? License key sẽ được tạo tự động với hạn sử dụng 3 tiếng.`,
                okText: 'Duyệt',
                okType: 'primary',
                cancelText: 'Hủy',
                onOk: async () => {
                    setSubmitting(true);
                    let [error, response] = await handleRequest(
                        builderLicenseApi.status(item.id, {status: 1})
                    );
                    let message = apiError('Duyệt license thất bại', error, response);
                    if (!message) {
                        dispatch(builderLicenseActions.update(response.data));
                        setSelectedItem(response.data);
                        notification.success({message: 'Đã duyệt và tạo license key thành công'});
                    }
                    setSubmitting(false);
                }
            });
        },
        reject: (item) => {
            AntModal.confirm({
                title: 'Xác nhận từ chối',
                content: `Từ chối license cho website "${item.websiteName}"?`,
                okText: 'Từ chối',
                okType: 'danger',
                cancelText: 'Hủy',
                onOk: async () => {
                    let [error, response] = await handleRequest(
                        builderLicenseApi.status(item.id, {status: 2})
                    );
                    let message = apiError('Từ chối thất bại', error, response);
                    if (!message) {
                        dispatch(builderLicenseActions.update(response.data));
                        setSelectedItem(response.data);
                        notification.success({message: 'Đã từ chối license'});
                    }
                }
            });
        },
        resetPending: async (item) => {
            setSubmitting(true);
            let [error, response] = await handleRequest(
                builderLicenseApi.status(item.id, {status: 0})
            );
            let message = apiError('Cập nhật trạng thái thất bại', error, response);
            if (!message) {
                dispatch(builderLicenseActions.update(response.data));
                setSelectedItem(response.data);
                notification.success({message: 'Đã đặt lại trạng thái chờ duyệt'});
            }
            setSubmitting(false);
        },
        delete: (id) => {
            AntModal.confirm({
                title: 'Xác nhận xóa',
                content: 'Bạn có chắc chắn muốn xóa builder license này không?',
                okText: 'Xóa',
                okType: 'danger',
                cancelText: 'Hủy',
                onOk: async () => {
                    let [error, response] = await handleRequest(builderLicenseApi.delete(id));
                    let message = apiError('Xóa builder license thất bại', error, response);
                    if (!message) {
                        dispatch(builderLicenseActions.delete(id));
                        notification.success({message: 'Xóa builder license thành công'});
                    }
                }
            });
        },
    };

    useEffect(() => {
        eventsHandler.load();
    }, [filter]);

    if (error) {
        notification.error({ message: 'Có lỗi xảy ra', description: error });
    }

    const statusCfg = selectedItem ? (statusConfig[selectedItem.status] ?? statusConfig[0]) : null;

    return (
        <>
            <ActionBar title={'Builder License'}>
                <Button outline small onClick={eventsHandler.load} style={{fontSize: '20px'}}>{Icon.reload}</Button>
            </ActionBar>
            {isMobile ? (
                <BuilderLicenseSearchMobile
                    filter={filter}
                    onSearchChange={eventsHandler.handleSearchChange}
                    onChange={eventsHandler.handleFilterChange}
                />
            ) : (
                <BuilderLicenseSearchBar
                    filter={filter}
                    onSearchChange={eventsHandler.handleSearchChange}
                    onChange={eventsHandler.handleFilterChange}
                />
            )}
            <div className="container">
                <div className="content">
                    {loading && <Loading noFixed spin/>}
                    {!loading && items && (
                        <BuilderLicenseTable
                            items={items}
                            pagination={pagination}
                            onPaginationChange={eventsHandler.changePagination}
                            can={can}
                            onDelete={eventsHandler.delete}
                            onViewDetail={eventsHandler.viewDetail}
                        />
                    )}
                </div>
            </div>

            {openModal && selectedItem && (
                <Modal
                    title={`Chi tiết Builder License #${selectedItem.id}`}
                    visible={openModal}
                    onCancel={eventsHandler.closeModal}
                    size="lg"
                    okButton={false}
                >
                    {submitting && <Loading/>}
                    <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="Tên Website">
                            <strong>{selectedItem.websiteName}</strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="Tên miền">
                            <a href={`https://${selectedItem.websiteUrl}`} target="_blank" rel="noreferrer">
                                {selectedItem.websiteUrl}
                            </a>
                        </Descriptions.Item>
                        <Descriptions.Item label="Trạng thái">
                            <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="License Key">
                            {selectedItem.license ? (
                                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                    <code style={{fontSize: 12, wordBreak: 'break-all', flex: 1}}>
                                        {selectedItem.license}
                                    </code>
                                    <Button
                                        small
                                        background
                                        blue
                                        leftIcon={Icon.copy}
                                        onClick={() => {
                                            navigator.clipboard.writeText(selectedItem.license);
                                            notification.success({message: 'Đã copy license key', duration: 1.5});
                                        }}
                                    >
                                        Copy
                                    </Button>
                                </div>
                            ) : (
                                <span style={{color: '#999'}}>Chưa có</span>
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Hạn sử dụng">
                            {selectedItem.expired ? renderDate(selectedItem.expired) : 'Vĩnh viễn'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ghi chú">
                            {selectedItem.note || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Elements đã dùng">
                            {(() => {
                                const list = selectedItem.elements
                                    ? (typeof selectedItem.elements === 'string'
                                        ? JSON.parse(selectedItem.elements)
                                        : selectedItem.elements)
                                    : [];
                                if (!list.length) return <span style={{color: '#999'}}>Không có</span>;
                                return (
                                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 4}}>
                                        {list.map(el => (
                                            <Tag key={el.id} color="blue" style={{margin: 0}}>
                                                {el.title}
                                            </Tag>
                                        ))}
                                    </div>
                                );
                            })()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Người tạo">
                            {selectedItem.user ? `${selectedItem.user.firstname} ${selectedItem.user.lastname}` : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ngày tạo">
                            {renderDate(selectedItem.created)}
                        </Descriptions.Item>
                    </Descriptions>

                    {can.status && (
                        <div style={{marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16}}>
                            {selectedItem.status === 0 && (
                                <Row gutter={8}>
                                    <Col>
                                        <Button green outline onClick={() => eventsHandler.approve(selectedItem)}>
                                            {Icon.success} Duyệt & Tạo License Key
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button red outline onClick={() => eventsHandler.reject(selectedItem)}>
                                            {Icon.close} Từ chối
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                            {selectedItem.status === 1 && (
                                <p style={{color: '#52c41a', margin: 0}}>✓ License đã được duyệt và kích hoạt.</p>
                            )}
                            {selectedItem.status === 2 && (
                                <Button outline onClick={() => eventsHandler.resetPending(selectedItem)}>
                                    Đặt lại Chờ duyệt
                                </Button>
                            )}
                        </div>
                    )}
                </Modal>
            )}
        </>
    );
}

export default BuilderLicense;

